import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Plus, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ExpandedState,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { PermissionGuard } from '@/components/PermissionGuard';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { useAuthStore } from '@features/authentication/store/authStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TableSkeleton } from '@/components/ui/skeleton';
import {
  SearchSuggestions,
  type SearchSuggestion,
} from '@/components/ui/search-suggestions';
import { useTableStateParams } from '@/hooks/useTableStateParams';
import { useReceivingStore } from '../store/receivingStore';
import { createReceivingColumns } from './receiving-table-columns';
import { toast } from 'sonner';
import type { ReceivingDocumentDto, ReceivingParentRow, ReceivingItemRow } from '../types/receiving.types';
import { ReceivingStatusEnum, PostingStatusEnum } from '../types/receiving.types';

const OPEN_STATUSES = new Set([
  ReceivingStatusEnum.PendingPutAway,
  ReceivingStatusEnum.PartiallyPutAway,
  ReceivingStatusEnum.PutAwayComplete,
]);

const CLOSED_STATUSES = new Set([
  ReceivingStatusEnum.Completed,
  ReceivingStatusEnum.Cancelled,
]);

function buildTableData(documents: ReceivingDocumentDto[]): ReceivingParentRow[] {
  return documents.map((doc): ReceivingParentRow => ({
    rowType: 'document',
    id: String(doc.id),
    document: doc,
    subRows: doc.items.map((item): ReceivingItemRow => ({
      rowType: 'item',
      id: `${doc.id}-${item.id}`,
      documentId: doc.id,
      item,
    })),
  }));
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface ReceivingSubTableProps {
  documents: ReceivingDocumentDto[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  showCreateButton: boolean;
  renderSearch: () => React.ReactNode;
  tab: 'open' | 'closed';
  sorting?: SortingState;
  onSortingChange?: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  pagination?: PaginationState;
  onPaginationChange?: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  onResync: (e: React.MouseEvent, docId: number) => void;
  isSubmitting: boolean;
}

function ReceivingSubTable({
  documents,
  isLoading,
  error,
  onRefresh,
  showCreateButton,
  renderSearch,
  tab,
  sorting: externalSorting,
  onSortingChange: externalOnSortingChange,
  pagination: externalPagination,
  onPaginationChange: externalOnPaginationChange,
  onResync,
  isSubmitting,
}: ReceivingSubTableProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const [localPagination, setLocalPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  const sorting = externalSorting ?? localSorting;
  const onSortingChange = externalOnSortingChange ?? setLocalSorting;
  const pagination = externalPagination ?? localPagination;
  const onPaginationChange = externalOnPaginationChange ?? setLocalPagination;

  const handleCreateReceiving = () => {
    navigate('/mro/receiving/new');
  };

  const fromParam = tab === 'closed' ? '?from=closed' : '';

  const handleView = (docId: number) => {
    navigate(`/mro/receiving/${docId}${fromParam}`);
  };

  const tableData = useMemo(
    () => buildTableData(documents),
    [documents],
  );

  // 86d3rrgt5 — Resync requires receiving.create; hide the list affordance for view-only users.
  const canResync = useAuthStore((s) => s.hasPermission(PERMISSIONS.RECEIVING_CREATE));
  const columns = useMemo(
    () =>
      createReceivingColumns({
        onView: handleView,
        onResync,
        isSubmitting,
        tab,
        canResync,
      }),
    [isSubmitting, tab, canResync]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSubRows: (row) => (row.rowType === 'document' ? row.subRows : undefined),
    onExpandedChange: setExpanded,
    onSortingChange,
    onPaginationChange,
    getRowId: (row) => row.id,
    autoResetPageIndex: false,
    autoResetExpanded: false,
    state: {
      expanded,
      sorting,
      pagination,
    },
  });

  // First/empty load — show a table-shaped skeleton. On refetch with data
  // already in hand, keep the existing rows (stale-data-plus-skeleton).
  if (isLoading && documents.length === 0) {
    return <TableSkeleton rows={8} cols={8} className="mt-2" />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-destructive mb-4">{error}</p>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {showCreateButton
              ? 'Manage receiving documents for purchase order deliveries'
              : 'View completed and cancelled receiving documents'}
          </p>
          {showCreateButton && (
            <PermissionGuard permission={PERMISSIONS.RECEIVING_CREATE}>
              <Button onClick={handleCreateReceiving}>
                <Plus className="h-4 w-4 mr-2" />
                New Receiving
              </Button>
            </PermissionGuard>
          )}
        </div>
        <div className="flex flex-col items-center justify-center h-64 border rounded-lg bg-muted/10">
          <Package className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {showCreateButton ? 'No Open Documents' : 'No Closed Documents'}
          </h3>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
            {showCreateButton
              ? 'There are no active receiving documents. Create a new one to get started.'
              : 'There are no completed or cancelled receiving documents yet.'}
          </p>
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  const displayCount = documents.length;
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      <div className="flex items-center justify-between gap-4">
        {renderSearch()}
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {displayCount} document{displayCount !== 1 ? 's' : ''}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {showCreateButton && (
              <PermissionGuard permission={PERMISSIONS.RECEIVING_CREATE}>
                <Button size="sm" onClick={handleCreateReceiving}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Receiving
                </Button>
              </PermissionGuard>
            )}
          </div>
        </div>
      </div>

      {/* Expandable Table — flat: row dividers only, no outer box */}
      <div>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={
                    row.original.rowType === 'item'
                      ? 'bg-muted/50 hover:bg-muted/70 !border-l-4 !border-l-primary/30'
                      : 'cursor-pointer hover:bg-muted/30'
                  }
                  onClick={() => {
                    if (row.original.rowType === 'document') {
                      row.toggleExpanded();
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={row.original.rowType === 'item' ? 'py-2 text-sm' : ''}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No documents found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(value) => {
              onPaginationChange((prev) => ({ ...prev, pageSize: Number(value), pageIndex: 0 }));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {pageCount || 1}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReceivingDocumentsTab() {
  const {
    allDocuments,
    isLoading,
    error,
    fetchAllDocuments,
    resyncDocument,
    isSubmitting,
    viewAll,
    setViewAll,
  } = useReceivingStore();

  const canViewAllReceiving = useAuthStore((s) => s.hasPermission(PERMISSIONS.RECEIVING_VIEW_ALL));

  // URL-persisted table state for the open tab
  const {
    sorting: openSorting,
    pagination: openPagination,
    onSortingChange: onOpenSortingChange,
    onPaginationChange: onOpenPaginationChange,
  } = useTableStateParams({ prefix: 'open', defaultPageSize: 10 });

  // URL-persisted table state for the closed tab
  const {
    sorting: closedSorting,
    pagination: closedPagination,
    onSortingChange: onClosedSortingChange,
    onPaginationChange: onClosedPaginationChange,
  } = useTableStateParams({ prefix: 'closed', defaultPageSize: 10 });

  // URL-persisted active tab
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'closed' ? 'closed' : 'open';

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'open') {
          next.delete('tab');
        } else {
          next.set('tab', value);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    fetchAllDocuments();
  }, [fetchAllDocuments, viewAll]);

  // Derive open and closed documents from allDocuments
  const openDocuments = useMemo(
    () => allDocuments.filter((doc) => OPEN_STATUSES.has(doc.receivingStatus)),
    [allDocuments],
  );

  const closedDocuments = useMemo(
    () => allDocuments.filter((doc) => CLOSED_STATUSES.has(doc.receivingStatus)),
    [allDocuments],
  );

  // 86d3kwtar — async posting has no realtime push yet (poll-based until the notification service
  // lands). While ANY document is mid-post, refresh the list every 3s so the table reflects the
  // worker settling — including a crash-recovery that completes server-side, or a post driven from
  // another session — without a manual refresh. Self-terminates the instant nothing is in-flight
  // (anyPosting flips false → effect cleanup clears the interval).
  const anyPosting = useMemo(
    () => allDocuments.some((doc) => doc.postingStatus === PostingStatusEnum.Posting),
    [allDocuments],
  );

  useEffect(() => {
    if (!anyPosting) return;
    const timer = setInterval(() => {
      fetchAllDocuments();
    }, 3000);
    return () => clearInterval(timer);
  }, [anyPosting, fetchAllDocuments]);

  // Resync handler
  const handleResync = useCallback(
    async (e: React.MouseEvent, docId: number) => {
      e.stopPropagation();
      try {
        const result = await resyncDocument(docId);
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.warning(result.message);
        }
      } catch {
        // error shown via store
      }
    },
    [resyncDocument]
  );

  // Client-side search suggestions for open documents
  const getOpenSuggestions = useCallback(
    (query: string): SearchSuggestion[] => {
      const normalized = query.toLowerCase().trim();
      if (!normalized) return [];

      return openDocuments
        .filter((doc) => {
          if (doc.docNo.toLowerCase().includes(normalized)) return true;
          if (doc.purchaseOrderNumber.toLowerCase().includes(normalized)) return true;
          if (doc.vendorName.toLowerCase().includes(normalized)) return true;
          if (doc.deliveryNumber.toLowerCase().includes(normalized)) return true;
          return false;
        })
        .map((doc) => ({
          id: doc.id,
          title: doc.docNo,
          subtitle: `PO: ${doc.purchaseOrderNumber} · ${doc.vendorName} · ${doc.items.length} item${doc.items.length !== 1 ? 's' : ''}`,
          href: `/mro/receiving/${doc.id}`,
        }));
    },
    [openDocuments]
  );

  // Client-side search suggestions for closed documents
  const getClosedSuggestions = useCallback(
    (query: string): SearchSuggestion[] => {
      const normalized = query.toLowerCase().trim();
      if (!normalized) return [];

      return closedDocuments
        .filter((doc) => {
          if (doc.docNo.toLowerCase().includes(normalized)) return true;
          if (doc.purchaseOrderNumber.toLowerCase().includes(normalized)) return true;
          if (doc.vendorName.toLowerCase().includes(normalized)) return true;
          if (doc.deliveryNumber.toLowerCase().includes(normalized)) return true;
          return false;
        })
        .map((doc) => ({
          id: doc.id,
          title: doc.docNo,
          subtitle: `PO: ${doc.purchaseOrderNumber} · ${doc.vendorName} · ${doc.items.length} item${doc.items.length !== 1 ? 's' : ''}`,
          href: `/mro/receiving/${doc.id}`,
        }));
    },
    [closedDocuments]
  );

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
      <div className="flex items-center justify-between">
        <TabsList>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
        </TabsList>
        {canViewAllReceiving && (
          <div className="flex items-center gap-2">
            <Switch
              id="receiving-mine-only"
              checked={!viewAll}
              onCheckedChange={(checked) => setViewAll(!checked)}
            />
            <Label htmlFor="receiving-mine-only" className="cursor-pointer text-sm font-normal">
              Show only my receiving documents
            </Label>
          </div>
        )}
      </div>

      <TabsContent value="open">
        <ReceivingSubTable
          documents={openDocuments}
          isLoading={isLoading}
          error={error}
          onRefresh={fetchAllDocuments}
          showCreateButton={true}
          tab="open"
          sorting={openSorting}
          onSortingChange={onOpenSortingChange}
          pagination={openPagination}
          onPaginationChange={onOpenPaginationChange}
          onResync={handleResync}
          isSubmitting={isSubmitting}
          renderSearch={() => (
            <SearchSuggestions
              getSuggestions={getOpenSuggestions}
              placeholder="Search by doc#, PO#, vendor, or DR#..."
              className="w-72"
              inputClassName="h-9"
            />
          )}
        />
      </TabsContent>

      <TabsContent value="closed">
        <ReceivingSubTable
          documents={closedDocuments}
          isLoading={isLoading}
          error={error}
          onRefresh={fetchAllDocuments}
          showCreateButton={false}
          tab="closed"
          sorting={closedSorting}
          onSortingChange={onClosedSortingChange}
          pagination={closedPagination}
          onPaginationChange={onClosedPaginationChange}
          onResync={handleResync}
          isSubmitting={isSubmitting}
          renderSearch={() => (
            <SearchSuggestions
              getSuggestions={getClosedSuggestions}
              placeholder="Search by doc#, PO#, vendor, or DR#..."
              className="w-72"
              inputClassName="h-9"
            />
          )}
        />
      </TabsContent>
    </Tabs>
  );
}
